import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { Building2, EllipsisVertical, Eye, MailPlus, Network, Pencil } from "lucide-react";
import { isJobDemandSide } from "../workerLine/workerLineShared.js";

/**
 * P165: the จัดการ menu on one Super Agent row.
 *
 * Owner's mockup puts a kebab at the end of every row. What it holds is
 * deliberately only what this screen can really do today - open the record,
 * edit it, see its line as a chart, jump to the บริษัท under it, and (P167) send
 * that line's partner invite letter. There is no export, no archive and no "send
 * message" item, because no API behind this page offers any of those and a menu
 * entry that cannot work is worse than a shorter menu.
 *
 * The trigger stops its own click from reaching the row, which opens the drawer:
 * without that, pressing the kebab would open the record AND the menu, and the
 * drawer sliding in would take the menu with it.
 *
 * P167 adds "จดหมายเชิญพันธมิตร" here, at the owner's instruction that the menu
 * be folded into its parent rather than standing alone in the sidebar - the same
 * fold Worksites and Jobs got into Employers. A partner letter only ever exists
 * under one Super Agent line, so the row that names that line is where sending
 * it belongs. It is hidden on a job-side row: that line recruits employers, and
 * the API refuses a partner letter there with 409 (P156).
 *
 * WHY THE MENU IS IN A PORTAL. The table it sits in is `.wx-wl-table-wrap`,
 * which carries `overflow-x: auto` for narrow windows - and CSS turns the other
 * axis into `auto` too the moment one axis is not `visible`. So an absolutely
 * positioned menu on the LAST row was cut off at the table's bottom edge: the
 * owner's own screenshot showed two of the four items and nothing else, which is
 * how a menu item can be shipped and still be invisible. Rendering into
 * document.body with fixed coordinates puts it outside every clipping ancestor;
 * nothing about the table's scrolling has to change.
 */
export default function SuperAgentRowMenu({ item, onOpenDetail, onEdit }) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState(null);
  const containerRef = useRef(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  const GAP = 4;

  /**
   * Anchored to the trigger's own viewport rect, right-aligned to it (the kebab
   * is the last thing in the row, so opening leftwards is what keeps the menu
   * on screen), and flipped above the trigger when there is not enough room
   * below - which is exactly the last-row case that was being clipped.
   */
  const place = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger || typeof window === "undefined") {
      return;
    }
    const rect = trigger.getBoundingClientRect();
    const menuHeight = menuRef.current?.offsetHeight ?? 0;
    const roomBelow = window.innerHeight - rect.bottom;
    const flipUp = menuHeight > 0 && roomBelow < menuHeight + GAP && rect.top > menuHeight + GAP;

    setPosition({
      top: flipUp ? rect.top - menuHeight - GAP : rect.bottom + GAP,
      right: Math.max(window.innerWidth - rect.right, GAP),
    });
  }, []);

  // Measured after the menu is in the DOM, so the flip decision uses its real
  // height rather than a guess that would be wrong the moment an item is added.
  useLayoutEffect(() => {
    if (open) {
      place();
    } else {
      setPosition(null);
    }
  }, [open, place]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const onPointerDown = (event) => {
      // The menu is a portal, so it is NOT inside containerRef - checking only
      // the container would treat a click on a menu item as an outside click,
      // unmount the item on mousedown, and the click would never land on it.
      const insideTrigger = containerRef.current?.contains(event.target);
      const insideMenu = menuRef.current?.contains(event.target);
      if (!insideTrigger && !insideMenu) {
        setOpen(false);
      }
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        // Consumed on the capture phase: the drawer and this page both listen
        // for Escape, and one press should close the menu only.
        event.stopImmediatePropagation();
        setOpen(false);
      }
    };
    // Fixed coordinates go stale the moment anything scrolls, and a menu left
    // hanging beside its row is worse than one that closes.
    const onScrollOrResize = () => setOpen(false);

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown, true);
    window.addEventListener("resize", onScrollOrResize);
    window.addEventListener("scroll", onScrollOrResize, true);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown, true);
      window.removeEventListener("resize", onScrollOrResize);
      window.removeEventListener("scroll", onScrollOrResize, true);
    };
  }, [open]);

  const close = (event) => {
    event?.stopPropagation();
    setOpen(false);
  };

  const menu = (
    <div
      className="wx-wl-rowmenu-list wx-wl-rowmenu-list--floating"
      role="menu"
      ref={menuRef}
      style={position ? { top: `${position.top}px`, right: `${position.right}px` } : { visibility: "hidden" }}
      onClick={(event) => event.stopPropagation()}
    >
      <button
        type="button"
        role="menuitem"
        className="wx-wl-rowmenu-item"
        onClick={(event) => {
          close(event);
          onOpenDetail(item.superAgentOrganizationId);
        }}
      >
        <Eye size={14} aria-hidden="true" />
        ดูรายละเอียด
      </button>

      <button
        type="button"
        role="menuitem"
        className="wx-wl-rowmenu-item"
        onClick={(event) => {
          close(event);
          onEdit(item.superAgentOrganizationId);
        }}
      >
        <Pencil size={14} aria-hidden="true" />
        แก้ไขข้อมูล
      </button>

      <Link
        role="menuitem"
        className="wx-wl-rowmenu-item"
        to={`/backoffice/super-agents/${item.superAgentOrganizationId}`}
        onClick={close}
      >
        <Network size={14} aria-hidden="true" />
        เปิดผังสายงานนี้
      </Link>

      <Link
        role="menuitem"
        className="wx-wl-rowmenu-item"
        to={`/backoffice/worker-line/plcs?superAgentOrganizationId=${item.superAgentOrganizationId}`}
        onClick={close}
      >
        <Building2 size={14} aria-hidden="true" />
        ดูบริษัทในสายงานนี้
      </Link>

      {!isJobDemandSide(item.organizationSide) && (
        <Link
          role="menuitem"
          className="wx-wl-rowmenu-item"
          to={`/backoffice/partner-invite-cards?superAgentId=${encodeURIComponent(item.superAgentOrganizationId)}`}
          onClick={close}
        >
          <MailPlus size={14} aria-hidden="true" />
          จดหมายเชิญพันธมิตร
        </Link>
      )}
    </div>
  );

  return (
    <div className="wx-wl-rowmenu" ref={containerRef} onClick={(event) => event.stopPropagation()}>
      <button
        type="button"
        ref={triggerRef}
        className="wx-wl-rowmenu-trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`จัดการ ${item.displayName}`}
        onClick={(event) => {
          event.stopPropagation();
          setOpen((previous) => !previous);
        }}
      >
        <EllipsisVertical size={16} aria-hidden="true" />
      </button>

      {open && typeof document !== "undefined" && createPortal(menu, document.body)}
    </div>
  );
}
